import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function setMeta(attr, name, content) {
  if (content == null) return;
  const selector = `meta[${attr}="${name}"]`;
  const found = [...document.head.querySelectorAll(selector)];
  const el = found.shift() || document.createElement("meta");
  found.forEach((item) => item.remove());
  el.setAttribute(attr, name);
  el.setAttribute("content", content);
  if (!el.isConnected) document.head.appendChild(el);
}

function syncStructuredData(data) {
  const matches = [...document.head.querySelectorAll('script[data-bp-seo-ld="1"]')];
  const current = matches.shift();
  matches.forEach((el) => el.remove());

  if (!data) {
    current?.remove();
    return;
  }

  const el = current || document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute("data-bp-seo-ld", "1");
  el.textContent = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
  if (!el.isConnected) document.head.appendChild(el);
}

function applySeo(seo) {
  document.documentElement.lang = seo.lang;
  document.title = seo.title;

  setMeta("name", "description", seo.description);
  setMeta("property", "og:title", seo.title);
  setMeta("property", "og:description", seo.description);
  setMeta("property", "og:url", seo.canonical);
  setMeta("property", "og:locale", seo.ogLocale);
  setMeta("name", "twitter:title", seo.title);
  setMeta("name", "twitter:description", seo.description);

  const canonicalLinks = [
    ...document.head.querySelectorAll('link[rel="canonical"]'),
  ];
  const canonical = canonicalLinks.shift() || document.createElement("link");
  canonicalLinks.forEach((item) => item.remove());
  canonical.setAttribute("rel", "canonical");
  canonical.setAttribute("href", seo.canonical);
  if (!canonical.isConnected) document.head.appendChild(canonical);

  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((item) => item.remove());

  for (const item of seo.hreflangs || []) {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", item.lang);
    link.setAttribute("href", item.href);
    document.head.appendChild(link);
  }

  syncStructuredData(seo.structuredData);
  window.dispatchEvent(
    new CustomEvent("bestplace:seo-updated", { detail: seo })
  );
}

export default function SeoRouteHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const host = window.location.hostname;
    const allowedHosts = [
      "best-place.club", "www.best-place.club", "localhost", "127.0.0.1",
    ];
    if (!allowedHosts.includes(host)) return;

    const controller = new AbortController();

    async function syncSeo() {
      try {
        const response = await fetch(
          `/api/public/seo-meta?path=${encodeURIComponent(pathname)}`,
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;
        if (!response.ok) {
          if (response.status === 404) {
            // Не залишаємо JSON-LD попередньої сторінки після SPA-переходу.
            syncStructuredData(null);
            window.dispatchEvent(new CustomEvent("bestplace:seo-updated", {
              detail: { path: pathname, breadcrumbs: [] },
            }));
          }
          return;
        }

        const seo = await response.json();
        if (!controller.signal.aborted) applySeo(seo);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("SEO sync failed:", error);
        }
      }
    }

    syncSeo();
    return () => controller.abort();
  }, [pathname]);

  return null;
}
